/**
 * How It Works — the definitive canonical "System" page for the Ekselens
 * landscape. A single seven-beat narrative arc, every beat rendered from the
 * single source of truth in `data/system360.js`:
 *   1. System of Proof (the discipline & the promise)
 *   2. The eight-phase delivery lifecycle
 *   3. Ekselens360 service lines (12 lenses)
 *   4. Enterprise360 value streams (12)
 *   5. The Delivery Matrix (12 × 12, L/S/M)
 *   6. Evidence & assurance
 *   7. What we deliberately do not do (registry decisions + per-stream boundaries)
 *
 * Owner-locked hierarchy (Model B): Ekselens Consulting › System of Proof ›
 * EKSELENS360 & Enterprise360 › ERIO. Built on the existing `ek-` tokens.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, FileText, ShieldCheck, CheckCircle2, MinusCircle, CircleSlash, Eye } from "lucide-react";
import { LENSES } from "@/data/family360";
import {
  PHASES, LENS_ORDER, SERVICE_LINES, STREAMS, MATRIX, MATRIX_LEGEND, ASSURANCE, REGISTRY_DECISIONS,
} from "@/data/system360";
import { PhaseSpine, PHASE_ICONS } from "@/components/PhaseSpine";
import { ServiceLineIcon, ValueStreamIcon } from "@/components/DeliveryIcons";

const PURPLE = "#7737F6";
const CELL = {
  L: "bg-[#7737F6] text-white",
  S: "bg-[#EDE6FE] text-[#5D28C7]",
  M: "bg-[#F4EEFE] text-[#9B8BC9]",
};
const LENS_META = Object.fromEntries(LENSES.map((l) => [l.code, l]));

// ── Beat 1 · Hierarchy strip ────────────────────────────────────────────────
const Beat = ({ n, eyebrow, title, sub, children, dark }) => (
  <section className={dark ? "bg-[#0B0B0C] text-white" : "bg-white"} data-testid={`beat-${n}`}>
    <div className="ek-container py-20">
      <div className="flex items-baseline gap-3">
        <span className="text-[13px] font-bold tracking-[0.2em] text-[#A179FA]">BEAT {n}</span>
        <span className={`ek-eyebrow ${dark ? "text-[#A179FA]" : "text-[#7737F6]"}`}>{eyebrow}</span>
      </div>
      <h2 className={`ek-heading text-3xl md:text-4xl mt-3 max-w-3xl ${dark ? "text-white" : "text-black"}`}>{title}</h2>
      {sub && <p className={`text-lg font-light leading-relaxed mt-4 max-w-2xl ${dark ? "text-zinc-300" : "ek-body"}`}>{sub}</p>}
      <div className="mt-10">{children}</div>
    </div>
  </section>
);

const LeadChips = ({ codes, minor }) => (
  <span className="inline-flex flex-wrap gap-1.5 align-middle">
    {codes.map((c) => (
      <span key={c} className={`text-[10px] font-semibold px-2 py-0.5 rounded-sm ${minor ? "bg-[#F4EEFE] text-[#9B8BC9]" : "bg-[#EDE6FE] text-[#5D28C7]"}`}>{c}</span>
    ))}
  </span>
);

// ── Beat 5 · Delivery Matrix ────────────────────────────────────────────────
const DeliveryMatrix = () => {
  const [showMinor, setShowMinor] = useState(false);
  return (
    <div data-testid="delivery-matrix">
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-6" data-testid="matrix-legend">
        {MATRIX_LEGEND.map((l) => (
          <span key={l.key} className="flex items-center gap-2 text-[13px] text-zinc-700">
            <span className={`h-6 w-6 rounded-sm flex items-center justify-center text-[11px] font-bold ${CELL[l.key]}`}>{l.key}</span>
            <span><strong className="text-black">{l.label}</strong> — {l.note}</span>
          </span>
        ))}
      </div>

      {/* Desktop: full 12 × 12 grid */}
      <div className="hidden lg:block overflow-hidden border border-zinc-200 rounded-md" data-testid="matrix-grid-desktop">
        <table className="w-full border-collapse text-center">
          <thead>
            <tr className="bg-zinc-50">
              <th className="text-left text-[11px] uppercase tracking-[0.1em] text-zinc-500 font-bold px-3 py-3 border-b-2 border-black">Value stream</th>
              {LENS_ORDER.map((c) => (
                <th key={c} className="text-[9px] font-bold text-zinc-600 px-1 py-3 border-b-2 border-black align-bottom" title={SERVICE_LINES[c]}>
                  <span className="[writing-mode:vertical-rl] rotate-180 inline-block whitespace-nowrap">{c}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {STREAMS.map((s) => (
              <tr key={s.id} className="hover:bg-[#FBFAFF]" data-testid={`matrix-row-${s.id}`}>
                <th className="text-left px-3 py-2.5 border-b border-zinc-100" title={s.full}>
                  <span className="inline-flex items-center gap-2">
                    <ValueStreamIcon id={s.id} className="w-4 h-4 text-[#7737F6] shrink-0" />
                    <span className="text-[13px] font-semibold text-black">{s.short}</span>
                  </span>
                </th>
                {LENS_ORDER.map((c) => {
                  const v = MATRIX[s.id]?.[c];
                  return (
                    <td key={c} className="px-1 py-2.5 border-b border-zinc-100">
                      {v
                        ? <span className={`inline-flex h-7 w-7 items-center justify-center rounded-sm text-[11px] font-bold ${CELL[v]}`} title={`${c} · ${SERVICE_LINES[c]}`}>{v}</span>
                        : <span className="text-zinc-200">·</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile / tablet: condensed per-stream view */}
      <div className="lg:hidden space-y-3" data-testid="matrix-condensed-mobile">
        <label className="flex items-center gap-2 text-[13px] text-zinc-700 mb-2 cursor-pointer" data-testid="matrix-show-minor-toggle">
          <input type="checkbox" checked={showMinor} onChange={(e) => setShowMinor(e.target.checked)} className="accent-[#7737F6] h-4 w-4" />
          Show minor roles
        </label>
        {STREAMS.map((s) => {
          const cells = MATRIX[s.id] || {};
          const lead = LENS_ORDER.filter((c) => cells[c] === "L");
          const support = LENS_ORDER.filter((c) => cells[c] === "S");
          const minor = LENS_ORDER.filter((c) => cells[c] === "M");
          return (
            <div key={s.id} className="border border-zinc-200 rounded-md p-4" data-testid={`matrix-card-${s.id}`}>
              <div className="flex items-center gap-2 text-[14px] font-semibold text-black">
                <ValueStreamIcon id={s.id} className="w-4 h-4 text-[#7737F6] shrink-0" />
                {s.short}
              </div>
              <div className="mt-2 space-y-1.5 text-[12px]">
                <div className="flex flex-wrap items-center gap-1.5"><span className="text-zinc-500 w-14">Lead</span><LeadChips codes={lead} /></div>
                {support.length > 0 && <div className="flex flex-wrap items-center gap-1.5"><span className="text-zinc-500 w-14">Support</span><LeadChips codes={support} /></div>}
                {showMinor && minor.length > 0 && <div className="flex flex-wrap items-center gap-1.5"><span className="text-zinc-500 w-14">Minor</span><LeadChips codes={minor} minor /></div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TONE = {
  extension: { badge: "bg-[#EDE6FE] text-[#5D28C7]", icon: ArrowRight },
  folded: { badge: "bg-[#E6F2F0] text-[#0D7268]", icon: CheckCircle2 },
  watch: { badge: "bg-amber-100 text-amber-800", icon: Eye },
  out: { badge: "bg-zinc-200 text-zinc-700", icon: CircleSlash },
};

export default function HowItWorks() {
  const FE = process.env.REACT_APP_BACKEND_URL || "";
  return (
    <div data-testid="how-it-works-page">
      {/* Hero + Beat 1 */}
      <section className="bg-[#0B0B0C] text-white" data-testid="beat-1">
        <div className="ek-container py-24 md:py-32">
          <div className="ek-eyebrow text-[#A179FA]">The System</div>
          <h1 className="ek-heading text-5xl md:text-6xl lg:text-7xl text-white mt-5 max-w-4xl" data-testid="hiw-headline">
            One discipline. <span className="text-[#A179FA]">Two methodologies.</span> One system of proof.
          </h1>
          <p className="text-zinc-300 font-light text-lg md:text-xl leading-relaxed mt-7 max-w-2xl">
            Most transformation is sold on persuasion. The System of Proof — our parent discipline —
            turns it into proof. It is delivered two ways: EKSELENS360 goes deep by function,
            Enterprise360 goes end-to-end by value stream; and it separately informs ERIO, our
            risk-intelligence product. Here is the whole system, on one page, in seven beats.
          </p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-8 text-[13px] tracking-[0.04em] text-white/70" data-testid="hiw-hierarchy">
            <span>Ekselens Consulting</span><span className="text-white/35">›</span>
            <span className="text-[#A179FA] font-bold">System of Proof</span><span className="text-white/35">›</span>
            <span>EKSELENS360 &amp; Enterprise360</span><span className="text-white/35">›</span>
            <span>ERIO</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mt-12 max-w-3xl">
            {[
              { t: "The promise", d: "Every decision is traceable, every artefact defensible — and the value is realised, not just projected." },
              { t: "The discipline", d: "One maturity scale, one role taxonomy, one accountability spine — the same in every lens, every value stream and every board pack." },
              { t: "The proof", d: "A single source of truth, pre-export QA gates and a tamper-evident trail sit behind every deliverable we hand over." },
            ].map((c) => (
              <div key={c.t} className="border border-white/15 bg-white/[0.03] p-5">
                <ShieldCheck size={18} className="text-[#A179FA]" />
                <h3 className="ek-heading text-lg text-white mt-3">{c.t}</h3>
                <p className="text-[14px] text-zinc-400 mt-2 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-4 mt-10">
            <a href={`${FE}/EKSELENS360-Functional-Landscape.pdf`} target="_blank" rel="noreferrer" className="ek-btn ek-btn-primary" data-testid="hiw-download-landscape">
              Download the one-pager <Download size={16} />
            </a>
            <Link to="/value-stream-assessment" className="ek-btn ek-btn-outline-light" data-testid="hiw-assess-cta">
              Start a free assessment <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Beat 2 · Eight-phase lifecycle */}
      <Beat n="2" eyebrow="The lifecycle" title="Eight phases, one disciplined arc"
        sub="Every engagement — a lite self-assessment or a multi-year reinvention — runs the same eight phases. The same spine appears in every artefact we ship, so the method never changes shape between the website, the workshop and the boardroom.">
        <PhaseSpine className="mb-8" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="lifecycle-phases">
          {PHASES.map((p) => {
            const Icon = PHASE_ICONS[p.name];
            return (
              <div key={p.n} className="border border-zinc-200 border-t-4 border-t-[#7737F6] bg-white p-5" data-testid={`phase-${p.n}`}>
                <div className="flex items-center justify-between">
                  <div className="text-[28px] ek-heading text-[#D9CEFB] leading-none">{p.n}</div>
                  {Icon && <Icon className="w-8 h-8 text-[#7737F6]" />}
                </div>
                <h3 className="ek-heading text-xl text-black mt-2">{p.name}</h3>
                <p className="ek-body text-[13px] mt-2 leading-relaxed">{p.what}</p>
              </div>
            );
          })}
        </div>
      </Beat>

      {/* Beat 3 · EKSELENS360 service lines */}
      <section className="bg-zinc-50 border-y border-zinc-200" data-testid="beat-3">
        <div className="ek-container py-20">
          <div className="flex items-baseline gap-3">
            <span className="text-[13px] font-bold tracking-[0.2em] text-[#A179FA]">BEAT 3</span>
            <span className="ek-eyebrow text-[#7737F6]">The function view</span>
          </div>
          <h2 className="ek-heading text-3xl md:text-4xl text-black mt-3 max-w-3xl">EKSELENS360 — twelve specialist service lines</h2>
          <p className="ek-body text-lg mt-4 max-w-2xl">Each lens is a deep discipline with its own free, no-login lite self-assessment. They are the specialists that deliver the value streams.</p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="service-lines">
            {LENS_ORDER.map((code) => {
              const m = LENS_META[code];
              return (
                <Link key={code} to={m ? `/services/${m.serviceSlug}` : "#"} className="border border-zinc-200 bg-white p-5 hover:border-[#7737F6] transition-colors group" data-testid={`service-line-${code}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <ServiceLineIcon code={code} className="w-6 h-6 text-[#7737F6] shrink-0" />
                      <span className="text-[11px] font-bold tracking-wide text-[#5D28C7] bg-[#EDE6FE] px-2 py-1 rounded-sm">{code}</span>
                    </div>
                    <ArrowRight size={15} className="text-zinc-300 group-hover:text-[#7737F6] transition-colors" />
                  </div>
                  <h3 className="ek-heading text-base text-black mt-3">{SERVICE_LINES[code]}</h3>
                  {m && <p className="text-[13px] text-zinc-500 mt-1.5">{m.tagline}</p>}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Beat 4 · Enterprise360 value streams */}
      <Beat n="4" eyebrow="The value-stream view" title="Enterprise360 — twelve end-to-end value streams"
        sub="The reinvention engine composes these end-to-end streams for the client's archetype. Each carries the short name buyers navigate by and the full scoping name that makes it defensible.">
        <div className="grid md:grid-cols-2 gap-4" data-testid="value-streams">
          {STREAMS.map((s) => (
            <div key={s.id} className="border border-zinc-200 border-l-4 border-l-[#7737F6] bg-white p-5" data-testid={`value-stream-${s.id}`}>
              <div className="flex items-start gap-3">
                <ValueStreamIcon id={s.id} className="w-6 h-6 text-[#7737F6] shrink-0 mt-0.5" />
                <div>
                  <h3 className="ek-heading text-lg text-black">{s.short}</h3>
                  {s.full !== s.short && <div className="text-[12px] font-medium text-[#7737F6] mt-0.5">{s.full}</div>}
                </div>
              </div>
              <p className="ek-body text-[13px] mt-2 leading-relaxed">{s.definition}</p>
              <div className="flex flex-wrap items-center gap-1.5 mt-3">
                <span className="text-[10px] uppercase tracking-wide text-zinc-400 font-semibold">Led by</span>
                <LeadChips codes={s.lead} />
              </div>
            </div>
          ))}
        </div>
      </Beat>

      {/* Beat 5 · Delivery Matrix */}
      <section className="bg-zinc-50 border-y border-zinc-200" data-testid="beat-5">
        <div className="ek-container py-20">
          <div className="flex items-baseline gap-3">
            <span className="text-[13px] font-bold tracking-[0.2em] text-[#A179FA]">BEAT 5</span>
            <span className="ek-eyebrow text-[#7737F6]">Where the two arms meet</span>
          </div>
          <h2 className="ek-heading text-3xl md:text-4xl text-black mt-3 max-w-3xl">The Delivery Matrix</h2>
          <p className="ek-body text-lg mt-4 max-w-3xl">Two methodologies only earn their keep if the seams between them are clean. This grid is the seam made visible: for each of the twelve value streams it names which of the twelve service lines <strong className="text-black">leads</strong> the outcome, which <strong className="text-black">support</strong> it and which <strong className="text-black">advise</strong>. No ambiguity about who owns what — and nothing left to fall through the gaps.</p>
          <div className="mt-10"><DeliveryMatrix /></div>
        </div>
      </section>

      {/* Beat 6 · Evidence & assurance */}
      <Beat n="6" eyebrow="Why it holds up" title="Evidence &amp; assurance" dark
        sub="The System of Proof is not a slogan — it is engineered into every deliverable. Six mechanisms make the output defensible under scrutiny.">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="assurance">
          {ASSURANCE.map((a) => (
            <div key={a.t} className="border border-white/15 bg-white/[0.03] p-5" data-testid={`assurance-${a.t.split(" ")[0].toLowerCase()}`}>
              <ShieldCheck size={18} className="text-[#A179FA]" />
              <h3 className="ek-heading text-lg text-white mt-3">{a.t}</h3>
              <p className="text-[13px] text-zinc-400 mt-2 leading-relaxed">{a.d}</p>
            </div>
          ))}
        </div>
      </Beat>

      {/* Beat 7 · What we deliberately do not do */}
      <section className="bg-white" data-testid="beat-7">
        <div className="ek-container py-20">
          <div className="flex items-baseline gap-3">
            <span className="text-[13px] font-bold tracking-[0.2em] text-[#A179FA]">BEAT 7</span>
            <span className="ek-eyebrow text-[#7737F6]">Honest boundaries</span>
          </div>
          <h2 className="ek-heading text-3xl md:text-4xl text-black mt-3 max-w-3xl">What we deliberately do not do</h2>
          <p className="ek-body text-lg mt-4 max-w-3xl">A registry is only credible if it has edges. Ours is exactly twelve streams — so when a sharp buyer asks about a thirteenth, we have a position on record rather than a scramble. Some capabilities are folded into an existing stream, some we are watching, one is a deliberate referral to a specialist partner. Naming what we do not do is how we stay honest about what we do.</p>

          {/* Panel A — strategic exclusions (registry decisions) */}
          <h3 className="ek-heading text-xl text-black mt-10">Registry decisions — the candidate streams</h3>
          <div className="mt-5 space-y-3" data-testid="registry-decisions">
            {REGISTRY_DECISIONS.map((d) => {
              const tone = TONE[d.tone] || TONE.out;
              const Icon = tone.icon;
              return (
                <details key={d.slug} className="border border-zinc-200 rounded-md group" data-testid={`registry-decision-${d.slug}`}>
                  <summary className="flex flex-wrap items-center gap-3 px-5 py-4 cursor-pointer list-none">
                    <Icon size={18} className="text-[#7737F6] flex-none" />
                    <span className="ek-heading text-base text-black">{d.name}</span>
                    <span className="text-[12px] text-zinc-400">{d.alt}</span>
                    <span className={`ml-auto text-[11px] font-semibold px-2.5 py-1 rounded-full ${tone.badge}`}>{d.verdict}</span>
                  </summary>
                  <div className="px-5 pb-5 pt-1 border-t border-zinc-100">
                    <p className="text-[12px] text-zinc-500 mt-3"><strong className="text-zinc-700">Current home:</strong> {d.home}</p>
                    <p className="text-[12px] text-zinc-500 mt-1"><strong className="text-zinc-700">Delivery route:</strong> {d.route}</p>
                    <p className="ek-body text-[14px] mt-3 leading-relaxed">{d.rationale}</p>
                  </div>
                </details>
              );
            })}
          </div>

          {/* Panel B — per-stream tactical boundaries */}
          <h3 className="ek-heading text-xl text-black mt-12">Boundaries within each stream</h3>
          <p className="ek-body text-[14px] mt-2 max-w-3xl">Every stream also states, up front, what it does not own — so handoffs are clean and nothing falls through the cracks.</p>
          <div className="mt-5 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="stream-boundaries">
            {STREAMS.map((s) => (
              <div key={s.id} className="border border-zinc-200 bg-zinc-50 p-4" data-testid={`boundary-${s.id}`}>
                <div className="text-[13px] font-semibold text-black">{s.short}</div>
                <ul className="mt-2 space-y-1">
                  {s.outOfScope.map((o, i) => (
                    <li key={i} className="flex items-start gap-2 text-[12px] text-zinc-600">
                      <MinusCircle size={13} className="text-zinc-400 flex-none mt-0.5" /> {o}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0B0B0C] text-white" data-testid="hiw-cta">
        <div className="ek-container py-20 text-center">
          <h2 className="ek-heading text-3xl md:text-4xl text-white">See the whole system of proof.</h2>
          <p className="text-zinc-300 font-light text-lg mt-4 max-w-2xl mx-auto">Download the full asset pack — the functional landscape, the platform overview, the Manifesto, and the EGMM and MRM frameworks.</p>
          <div className="flex flex-wrap gap-4 justify-center mt-8">
            <a href={`${FE}/EKSELENS360-System-of-Proof-Pack.zip`} target="_blank" rel="noreferrer" className="ek-btn ek-btn-primary" data-testid="hiw-download-pack">
              Download the pack <FileText size={16} />
            </a>
            <Link to="/the-360-family" className="ek-btn ek-btn-outline-light" data-testid="hiw-family-cta">
              Explore the 360 family <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
